import { useForm } from 'react-hook-form';
import { userResolver } from './userScheme';
import { Alert, Button, Col, Row, Container } from 'react-bootstrap';
import PropTypes from 'prop-types';
import {
  FormProvider,
  RHFCheck,
  RHFControl,
  RHFSelect
} from 'components/common/form';
import { Card } from 'react-bootstrap/esm';
import PermissionItem from './PermissionItem';
import { useContext, useEffect, useMemo, useState } from 'react';
import AuthContext, { rolesMap } from 'context/authContext';
import {
  permissionsMap,
  groundTeamPermissions
} from 'routes/permissions/permissionsMap';
import { userActions } from 'routes/permissions/permissionKeys';
import { useBranches, useBranch } from 'hooks/useBranches';
import { checkUserExistsByEmail } from 'lib/supabaseQueries';

export default function UserForm({ onSubmit, loading, isEdit, initValues }) {
  const { organization, isOrganizationAdmin, selectedBranchId } =
    useContext(AuthContext);

  // Use Supabase hooks
  const { branches } = useBranches();
  const { branch: selectedBranch } = useBranch(
    !isOrganizationAdmin ? selectedBranchId : null
  );

  const branchOptions = useMemo(() => {
    if (!isOrganizationAdmin) {
      // For branch admins, return only their branch
      return [];
    }
    return branches.map(item => {
      return { label: item.name, value: item.id };
    });
  }, [branches, isOrganizationAdmin]);

  const permissionsInitialValues = useMemo(
    () =>
      permissionsMap.reduce((acc, currPermission) => {
        const { key: parentKey, children } = currPermission;

        // Add parent-level permissions
        const parentPerms = {
          [parentKey]: {
            [userActions.read]: false,
            [userActions.create]: false,
            [userActions.update]: false,
            [userActions.delete]: false
          }
        };

        // Add child-level permissions
        const childPerms = children.reduce((acc, curr) => {
          const { key, actions } = curr;
          const tmp = actions.reduce((acc, curr) => {
            return {
              ...acc,
              [curr]: false
            };
          }, {});
          return {
            ...acc,
            [key]: tmp
          };
        }, {});

        return {
          ...acc,
          ...parentPerms,
          ...childPerms
        };
      }, {}),
    []
  );

  const methods = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: initValues?.user?.first_name ?? '',
      lastName: initValues?.user?.last_name ?? '',
      branch: !isOrganizationAdmin ? selectedBranchId : '',
      email: '',
      password: '',
      confirmPassword: '',
      adminPassword: '',
      superAdmin: false,
      groundTeam: false,
      groundsTeamManager: false,
      permissions: permissionsInitialValues,
      isEdit,
      existingUser: false
    },
    resolver: userResolver
  });
  const { handleSubmit, setValue, watch } = methods;
  const superAdmin = watch('superAdmin');
  const groundTeam = watch('groundTeam');
  const groundsTeamManager = watch('groundsTeamManager');
  const emailValue = watch('email');

  // When adding (not editing), detect whether the typed email already belongs
  // to a user in this organisation. If so, they already have a login and we're
  // just assigning them to another branch - skip the password fields.
  const [existingUser, setExistingUser] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  useEffect(() => {
    if (isEdit || !organization?.id) return;

    const email = (emailValue || '').trim();
    const isValidEmail = /^\S+@\S+\.\S+$/.test(email);
    if (!isValidEmail) {
      setExistingUser(null);
      setCheckingEmail(false);
      setValue('existingUser', false);
      return;
    }

    let cancelled = false;
    setCheckingEmail(true);
    const timer = setTimeout(async () => {
      const { data } = await checkUserExistsByEmail(email, organization.id);
      if (cancelled) return;
      setExistingUser(data || null);
      setValue('existingUser', !!data);
      setCheckingEmail(false);
      // Prefill the name from the existing record (it isn't editable here -
      // the user already exists, we're only adding a branch role).
      if (data) {
        setValue('firstName', data.first_name || '');
        setValue('lastName', data.last_name || '');
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [emailValue, isEdit, organization?.id, setValue]);

  // When ground team or grounds team manager is toggled, apply preset permissions
  // (both roles share the same permission set — manager only adds approval ability)
  useEffect(() => {
    if (groundTeam || groundsTeamManager) {
      // Apply ground team permissions
      Object.entries(groundTeamPermissions).forEach(([key, value]) => {
        setValue(`permissions.${key}`, value);
      });

      // Update parent permissions based on children
      permissionsMap.forEach(({ key: parentKey, children }) => {
        const hasRead = children.some(
          child => groundTeamPermissions[child.key]?.[userActions.read]
        );
        const hasCreate = children.some(
          child => groundTeamPermissions[child.key]?.[userActions.create]
        );
        const hasUpdate = children.some(
          child => groundTeamPermissions[child.key]?.[userActions.update]
        );
        const hasDelete = children.some(
          child => groundTeamPermissions[child.key]?.[userActions.delete]
        );

        setValue(`permissions.${parentKey}.${userActions.read}`, hasRead);
        setValue(`permissions.${parentKey}.${userActions.create}`, hasCreate);
        setValue(`permissions.${parentKey}.${userActions.update}`, hasUpdate);
        setValue(`permissions.${parentKey}.${userActions.delete}`, hasDelete);
      });
    }
  }, [groundTeam, groundsTeamManager, setValue]);

  useEffect(() => {
    if (isEdit && initValues) {
      setValue('firstName', initValues?.user?.first_name);
      setValue('lastName', initValues?.user?.last_name);
      setValue('email', initValues?.user?.email);
      setValue('branch', initValues?.branch_id);
      const superAdmin = initValues?.role_id === rolesMap.admin.id;
      const isGroundTeam = initValues?.role_id === rolesMap.groundTeam.id;
      const isGroundsTeamManager =
        initValues?.role_id === rolesMap.groundsTeamManager.id;
      setValue('superAdmin', superAdmin);
      setValue('groundTeam', isGroundTeam);
      setValue('groundsTeamManager', isGroundsTeamManager);
      if (!superAdmin && initValues?.permissions) {
        // Handle permissions whether they are already parsed objects or JSON strings
        let parsedPermissions = {};
        if (typeof initValues.permissions === 'string') {
          try {
            parsedPermissions = JSON.parse(initValues.permissions);
          } catch (e) {
            console.error('Failed to parse permissions:', e);
            parsedPermissions = {};
          }
        } else if (typeof initValues.permissions === 'object') {
          parsedPermissions = initValues.permissions;
        }

        // Set child permissions
        setValue('permissions', parsedPermissions);

        // Calculate and set parent permissions based on children
        permissionsMap.forEach(({ key: parentKey, children }) => {
          const hasRead = children.some(
            child => parsedPermissions[child.key]?.[userActions.read]
          );
          const hasCreate = children.some(
            child => parsedPermissions[child.key]?.[userActions.create]
          );
          const hasUpdate = children.some(
            child => parsedPermissions[child.key]?.[userActions.update]
          );
          const hasDelete = children.some(
            child => parsedPermissions[child.key]?.[userActions.delete]
          );

          setValue(`permissions.${parentKey}.${userActions.read}`, hasRead);
          setValue(`permissions.${parentKey}.${userActions.create}`, hasCreate);
          setValue(`permissions.${parentKey}.${userActions.update}`, hasUpdate);
          setValue(`permissions.${parentKey}.${userActions.delete}`, hasDelete);
        });
      }
    }
  }, [initValues, isEdit, setValue]);

  const submitButtonLabel = isEdit ? 'Save' : 'Add Now';
  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Container fluid className="px-0">
        <Row>
          <Col sm={12} md={8}>
            <Card>
              <Card.Body>
                <div className="d-flex flex-column gap-3">
                  <Row className="g-3">
                    <Col md={6}>
                      <RHFControl
                        label={'First Name'}
                        name={'firstName'}
                        placeholder={'First Name'}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <RHFControl
                        label={'Last Name'}
                        name={'lastName'}
                        placeholder={'Last Name'}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      {isOrganizationAdmin ? (
                        <RHFSelect
                          label={'Branch'}
                          name={'branch'}
                          options={branchOptions}
                          required
                        />
                      ) : (
                        <div>
                          <label className="form-label">Branch</label>
                          <input
                            type="text"
                            className="form-control"
                            value={selectedBranch?.name || 'Loading...'}
                            disabled
                            readOnly
                          />
                        </div>
                      )}
                    </Col>
                    <Col md={6}>
                      <RHFControl
                        label={'Email Address'}
                        name={'email'}
                        placeholder={'Email Address'}
                        required
                        type={'email'}
                        disabled={isEdit}
                      />
                    </Col>
                  </Row>
                  {!isEdit && checkingEmail && (
                    <p className="text-muted fs--1 mb-0">Checking email…</p>
                  )}
                  {!isEdit && existingUser && (
                    <Alert variant="info" className="mb-0">
                      This person already has an account
                      {existingUser.first_name
                        ? ` (${existingUser.first_name} ${
                            existingUser.last_name || ''
                          })`
                        : ''}
                      . They'll be added to this branch with the role and
                      permissions below — no password needed.
                    </Alert>
                  )}
                  {!isEdit && !existingUser && (
                    <Row>
                      <Col md={6}>
                        <RHFControl
                          label={'Password'}
                          name={'password'}
                          placeholder={'Password'}
                          type="password"
                          required
                        />
                      </Col>
                      <Col md={6}>
                        <RHFControl
                          label={'Confirm Password'}
                          name={'confirmPassword'}
                          placeholder={'Confirm Password'}
                          type="password"
                          required
                        />
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <h5 className="mb-3">Role Presets</h5>
                    <div className="d-flex justify-content-between mb-2">
                      <div>
                        <span className="fw-semi-bold">Branch Admin</span>
                        <p className="text-muted fs--1 mb-0">
                          Full access to all features
                        </p>
                      </div>
                      <RHFCheck
                        name={'superAdmin'}
                        onChange={e => {
                          if (e.target.checked) {
                            setValue('groundTeam', false);
                            setValue('groundsTeamManager', false);
                          }
                        }}
                      />
                    </div>
                    <div className="d-flex justify-content-between mb-3">
                      <div>
                        <span className="fw-semi-bold">Ground Team</span>
                        <p className="text-muted fs--1 mb-0">
                          Facilities, WeConnect, beneficiaries (no donor access)
                        </p>
                      </div>
                      <RHFCheck
                        name={'groundTeam'}
                        disabled={superAdmin}
                        onChange={e => {
                          if (e.target.checked) {
                            setValue('superAdmin', false);
                            setValue('groundsTeamManager', false);
                          }
                        }}
                      />
                    </div>
                    <div className="d-flex justify-content-between mb-3">
                      <div>
                        <span className="fw-semi-bold">
                          Grounds Team Manager
                        </span>
                        <p className="text-muted fs--1 mb-0">
                          Same as Ground Team, plus can approve supporting
                          documents
                        </p>
                      </div>
                      <RHFCheck
                        name={'groundsTeamManager'}
                        disabled={superAdmin}
                        onChange={e => {
                          if (e.target.checked) {
                            setValue('superAdmin', false);
                            setValue('groundTeam', false);
                          }
                        }}
                      />
                    </div>
                    {superAdmin && (
                      <Col md={12} className="mb-3">
                        <RHFControl
                          label={'Verify Your Password'}
                          name={'adminPassword'}
                          placeholder={
                            'Enter your password to assign Branch Admin role'
                          }
                          type="password"
                          required
                          helpText="For security, please enter your current password to assign Branch Admin privileges"
                        />
                      </Col>
                    )}
                    {!superAdmin && (
                      <>
                        <hr />
                        <h5 className="mb-3">
                          Permissions
                          {groundTeam && (
                            <span className="badge bg-info ms-2 fs--1">
                              Ground Team Preset
                            </span>
                          )}
                          {groundsTeamManager && (
                            <span className="badge bg-info ms-2 fs--1">
                              Grounds Team Manager Preset
                            </span>
                          )}
                        </h5>
                        <hr />
                        {permissionsMap.map((item, index) => (
                          <div key={item.key}>
                            <PermissionItem data={item} watch={watch} />
                            {index < permissionsMap.length - 1 && <hr />}
                          </div>
                        ))}
                      </>
                    )}
                  </Row>
                </div>
              </Card.Body>
            </Card>
            <Card className="mt-3">
              <Card.Body>
                <div className="d-flex justify-content-between">
                  <h5 className="mb-0">User Roles</h5>
                  <Button
                    variant="primary"
                    className="px-4"
                    size="sm"
                    type="submit"
                  >
                    {loading ? 'Loading...' : submitButtonLabel}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </FormProvider>
  );
}

// props validation
UserForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isEdit: PropTypes.bool,
  initValues: PropTypes.object
};
